import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { 
  Plus, Shield, CheckCircle, 
  Lock, Globe, Settings, Key
} from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { Breadcrumb } from '@/components/ui/Breadcrumb'
import toast from 'react-hot-toast'

interface AuthProvider {
  id: string
  name: string
  provider_type: string
  is_active: boolean
  is_configured: boolean
  active_users: {
    count: number
    last_updated: string | null
  }
  tenant_id?: string
  client_id?: string
}

export function AuthProvidersPage() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [providers, setProviders] = useState<AuthProvider[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [showConfigModal, setShowConfigModal] = useState(false)
  const [showAddModal, setShowAddModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [selectedProvider, setSelectedProvider] = useState<AuthProvider | null>(null)
  const [isUpdating, setIsUpdating] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)

  // Form state for edit modal
  const [formData, setFormData] = useState({
    name: '',
    tenant_id: '',
    client_id: '',
    client_secret: ''
  })

  // Check if user is admin
  useEffect(() => {
    if (user && user.role !== 'admin' && user.role !== 'super_admin') {
      toast.error('Admin access required')
      navigate('/dashboard')
    }
  }, [user, navigate])

  // Fetch providers function (reusable)
  const fetchProviders = async () => {
    try {
      const response = await fetch('/api/v1/auth-providers/', {
        credentials: 'include',
      })

      if (response.ok) {
        const data = await response.json()
        setProviders(data || [])
      } else {
        setProviders([])
      }
    } catch (error) {
      console.error('Failed to fetch auth providers:', error)
      setProviders([])
      toast.error('Failed to load authentication providers')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchProviders()
  }, [])

  const handleConfigure = (provider: AuthProvider) => {
    if (provider.provider_type !== 'microsoft') {
      toast.error('Only Microsoft Entra provider can be configured')
      return
    }
    setSelectedProvider(provider)
    setFormData({
      name: provider.name,
      tenant_id: provider.tenant_id || '',
      client_id: provider.client_id || '',
      client_secret: '' // Don't populate password field
    })
    setShowConfigModal(true)
  }

  const handleUpdate = async () => {
    if (!selectedProvider) return

    setIsUpdating(true)
    try {
      const updateData: any = {
        name: formData.name
      }

      // Only include fields that have values
      if (formData.tenant_id) updateData.tenant_id = formData.tenant_id
      if (formData.client_id) updateData.client_id = formData.client_id
      if (formData.client_secret) updateData.client_secret = formData.client_secret

      const response = await fetch(`/api/v1/auth-providers/${selectedProvider.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(updateData)
      })

      if (response.ok) {
        toast.success('Provider updated successfully')
        setShowConfigModal(false)
        await fetchProviders()
      } else {
        const error = await response.json()
        toast.error(error.detail || 'Failed to update provider')
      }
    } catch (error) {
      console.error('Failed to update provider:', error)
      toast.error('Failed to update provider')
    } finally {
      setIsUpdating(false)
    }
  }

  const handleDeleteClick = (provider: AuthProvider) => {
    setSelectedProvider(provider)
    setShowDeleteModal(true)
  }

  const handleDelete = async () => {
    if (!selectedProvider) return

    setIsDeleting(true)
    try {
      const response = await fetch(`/api/v1/auth-providers/${selectedProvider.id}`, {
        method: 'DELETE',
        credentials: 'include',
      })

      if (response.ok) {
        toast.success('Provider deleted successfully')
        setShowDeleteModal(false)
        await fetchProviders()
      } else {
        const error = await response.json()
        toast.error(error.detail || 'Failed to delete provider')
      }
    } catch (error) {
      console.error('Failed to delete provider:', error)
      toast.error('Failed to delete provider')
    } finally {
      setIsDeleting(false)
    }
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Breadcrumb */}
        <Breadcrumb
          items={[
            { label: 'Settings', href: '/settings', icon: <Settings className="w-4 h-4" /> },
            { label: 'Authentication Providers', icon: <Key className="w-4 h-4" /> }
          ]}
        />
        
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              Authentication Providers
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Configure SSO and external authentication methods
            </p>
          </div>
          
          <button
            onClick={() => setShowAddModal(true)}
            className="px-4 py-2 bg-gradient-to-r from-[#5869ff] to-[#000742] text-white rounded-lg flex items-center gap-2 hover:shadow-lg transition-all duration-200"
          >
            <Plus className="w-5 h-5" />
            Add Provider
          </button>
        </div>

        {/* Providers Grid */}
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-gray-500 dark:text-gray-400">Loading providers...</div>
          </div>
        ) : providers.length === 0 ? (
          <div className="bg-white dark:bg-[#1a1f5c]/50 rounded-xl border border-gray-200 dark:border-[#5869ff]/20 p-12 text-center mb-8">
            <Shield className="w-12 h-12 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              No Authentication Providers Configured
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              Configure SSO providers to enable external authentication for your users.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            {providers.map((provider) => (
            <div
              key={provider.id}
              className="bg-white dark:bg-[#1a1f5c]/50 rounded-xl border border-gray-200 dark:border-[#5869ff]/20 p-6"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  {provider.provider_type === 'microsoft' && (
                    <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                      <svg className="w-6 h-6" viewBox="0 0 23 23">
                        <path fill="#f3f3f3" d="M0 0h10.931v10.931H0z"/>
                        <path fill="#f35325" d="M12.069 0H23v10.931H12.069z"/>
                        <path fill="#05a6f0" d="M0 12.069h10.931V23H0z"/>
                        <path fill="#ffba08" d="M12.069 12.069H23V23H12.069z"/>
                      </svg>
                    </div>
                  )}
                  {provider.provider_type === 'okta' && (
                    <div className="w-12 h-12 bg-gray-100 dark:bg-gray-800 rounded-lg flex items-center justify-center">
                      <Lock className="w-6 h-6 text-gray-400" />
                    </div>
                  )}
                  {provider.provider_type === 'google' && (
                    <div className="w-12 h-12 bg-gray-100 dark:bg-gray-800 rounded-lg flex items-center justify-center">
                      <Globe className="w-6 h-6 text-gray-400" />
                    </div>
                  )}
                  
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {provider.name}
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {provider.provider_type === 'microsoft' ? 'OAuth 2.0' : 'SAML 2.0'}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  {provider.is_configured ? (
                    <span className="px-2 py-1 text-xs font-medium text-green-700 dark:text-green-400 bg-green-100 dark:bg-green-900/30 rounded-full">
                      Configured
                    </span>
                  ) : (
                    <span className="px-2 py-1 text-xs font-medium text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 rounded-full">
                      Not Configured
                    </span>
                  )}
                </div>
              </div>

              <div className="mb-4">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600 dark:text-gray-400">Active Users</span>
                  <span className="font-semibold text-gray-900 dark:text-white">
                    {provider.active_users.count}
                  </span>
                </div>
              </div>

              <div className="flex gap-2">
                {provider.provider_type === 'microsoft' ? (
                  <>
                    <button
                      onClick={() => handleConfigure(provider)}
                      className="flex-1 px-3 py-2 border border-gray-300 dark:border-[#5869ff]/30 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-[#1a1f5c]/50 transition-colors text-sm font-medium"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeleteClick(provider)}
                      className="flex-1 px-3 py-2 border border-red-300 dark:border-red-900/30 text-red-600 dark:text-red-400 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors text-sm font-medium"
                    >
                      Delete
                    </button>
                  </>
                ) : (
                  <button
                    disabled
                    className="flex-1 px-3 py-2 bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500 rounded-lg cursor-not-allowed text-sm font-medium"
                  >
                    Configure
                  </button>
                )}
              </div>
            </div>
          ))}
          </div>
        )}

        {/* SSO Benefits */}
        <div className="bg-white dark:bg-[#1a1f5c]/50 rounded-xl border border-gray-200 dark:border-[#5869ff]/20 p-6">
          <div className="flex items-center gap-3 mb-4">
            <Shield className="w-6 h-6 text-[#5869ff]" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Single Sign-On (SSO) Benefits
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-green-500 mt-0.5" />
              <div>
                <p className="font-medium text-gray-900 dark:text-white">
                  Improved security with centralized authentication
                </p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-green-500 mt-0.5" />
              <div>
                <p className="font-medium text-gray-900 dark:text-white">
                  Reduced password fatigue for users
                </p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-green-500 mt-0.5" />
              <div>
                <p className="font-medium text-gray-900 dark:text-white">
                  Simplified user provisioning and deprovisioning
                </p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-green-500 mt-0.5" />
              <div>
                <p className="font-medium text-gray-900 dark:text-white">
                  Enhanced compliance and audit capabilities
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Configuration Modal */}
      {showConfigModal && selectedProvider && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white dark:bg-[#000742] rounded-xl shadow-2xl p-6 max-w-md w-full border border-gray-200 dark:border-[#5869ff]/20">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Edit Microsoft Entra Configuration
            </h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Provider Name
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-[#5869ff]/30 rounded-lg bg-white dark:bg-[#1a1f5c]/30 text-gray-900 dark:text-white"
                  placeholder="Provider name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Tenant ID
                </label>
                <input
                  type="text"
                  value={formData.tenant_id}
                  onChange={(e) => setFormData({ ...formData, tenant_id: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-[#5869ff]/30 rounded-lg bg-white dark:bg-[#1a1f5c]/30 text-gray-900 dark:text-white"
                  placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Client ID
                </label>
                <input
                  type="text"
                  value={formData.client_id}
                  onChange={(e) => setFormData({ ...formData, client_id: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-[#5869ff]/30 rounded-lg bg-white dark:bg-[#1a1f5c]/30 text-gray-900 dark:text-white"
                  placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Client Secret <span className="text-gray-500 text-xs">(leave empty to keep current)</span>
                </label>
                <input
                  type="password"
                  value={formData.client_secret}
                  onChange={(e) => setFormData({ ...formData, client_secret: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-[#5869ff]/30 rounded-lg bg-white dark:bg-[#1a1f5c]/30 text-gray-900 dark:text-white"
                  placeholder="••••••••••••••••"
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowConfigModal(false)}
                disabled={isUpdating}
                className="flex-1 px-4 py-2 border border-gray-300 dark:border-[#5869ff]/30 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-[#1a1f5c]/50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdate}
                disabled={isUpdating}
                className="flex-1 px-4 py-2 bg-gradient-to-r from-[#5869ff] to-[#000742] text-white rounded-lg hover:shadow-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isUpdating ? 'Saving...' : 'Save Configuration'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Provider Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white dark:bg-[#000742] rounded-xl shadow-2xl p-6 max-w-lg w-full border border-gray-200 dark:border-[#5869ff]/20">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Add Authentication Provider
            </h3>
            
            <form onSubmit={(e) => {
              e.preventDefault()
              const formData = new FormData(e.target as HTMLFormElement)
              
              const providerData = {
                name: formData.get('name') as string,
                provider_type: formData.get('provider_type') as string,
                client_id: formData.get('client_id') as string,
                client_secret: formData.get('client_secret') as string,
                tenant_id: formData.get('tenant_id') as string || undefined,
                scope: formData.get('scope') as string || 'openid profile email'
              }
              
              // Call API to create provider
              fetch('/api/v1/auth-providers/', {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify(providerData)
              })
              .then(response => {
                if (response.ok) {
                  toast.success('Provider added successfully')
                  setShowAddModal(false)
                  // Refresh providers list
                  const fetchProviders = async () => {
                    const response = await fetch('/api/v1/auth-providers/', {
                      credentials: 'include',
                    })
                    if (response.ok) {
                      const data = await response.json()
                      setProviders(data || [])
                    }
                  }
                  fetchProviders()
                } else {
                  response.json().then(err => {
                    toast.error(err.detail || 'Failed to add provider')
                  })
                }
              })
              .catch(() => {
                toast.error('Failed to add provider')
              })
            }}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Provider Name
                  </label>
                  <input
                    type="text"
                    name="name"
                    required
                    className="w-full px-3 py-2 border border-gray-300 dark:border-[#5869ff]/30 rounded-lg bg-white dark:bg-[#1a1f5c]/30 text-gray-900 dark:text-white"
                    placeholder="e.g., Company Microsoft Azure"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Provider Type
                  </label>
                  <select
                    name="provider_type"
                    required
                    className="w-full px-3 py-2 border border-gray-300 dark:border-[#5869ff]/30 rounded-lg bg-white dark:bg-[#1a1f5c]/30 text-gray-900 dark:text-white"
                  >
                    <option value="microsoft">Microsoft Azure AD / Entra</option>
                  </select>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Other providers (Google, Okta, Auth0) coming soon
                  </p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Client ID
                  </label>
                  <input
                    type="text"
                    name="client_id"
                    required
                    className="w-full px-3 py-2 border border-gray-300 dark:border-[#5869ff]/30 rounded-lg bg-white dark:bg-[#1a1f5c]/30 text-gray-900 dark:text-white"
                    placeholder="Application (client) ID"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Client Secret
                  </label>
                  <input
                    type="password"
                    name="client_secret"
                    required
                    className="w-full px-3 py-2 border border-gray-300 dark:border-[#5869ff]/30 rounded-lg bg-white dark:bg-[#1a1f5c]/30 text-gray-900 dark:text-white"
                    placeholder="Client secret value"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Tenant ID <span className="text-gray-500">(optional - leave empty for multi-tenant)</span>
                  </label>
                  <input
                    type="text"
                    name="tenant_id"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-[#5869ff]/30 rounded-lg bg-white dark:bg-[#1a1f5c]/30 text-gray-900 dark:text-white"
                    placeholder="Directory (tenant) ID or leave empty for multi-tenant"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Scope <span className="text-gray-500">(optional)</span>
                  </label>
                  <input
                    type="text"
                    name="scope"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-[#5869ff]/30 rounded-lg bg-white dark:bg-[#1a1f5c]/30 text-gray-900 dark:text-white"
                    placeholder="openid profile email"
                    defaultValue="openid profile email"
                  />
                </div>
              </div>

              {/* Redirect URI Information */}
              <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                <div className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  <div className="flex-1">
                    <h4 className="text-sm font-semibold text-blue-900 dark:text-blue-200 mb-2">
                      Redirect URI Configuration
                    </h4>
                    <p className="text-sm text-blue-800 dark:text-blue-300 mb-2">
                      Configure this Redirect URI in your Microsoft Entra app registration:
                    </p>
                    <div className="bg-white dark:bg-gray-800 rounded px-3 py-2 font-mono text-xs text-gray-800 dark:text-gray-200 border border-blue-300 dark:border-blue-700">
                      {window.location.protocol === 'https:'
                        ? `${window.location.origin}/api/v1/auth/sso/microsoft/callback`
                        : `https://${window.location.hostname}:8443/api/v1/auth/sso/microsoft/callback`}
                    </div>
                    <p className="text-xs text-blue-700 dark:text-blue-400 mt-2">
                      💡 Go to Azure Portal → App registrations → Authentication → Add a platform → Web → Add this URL
                    </p>
                    <p className="text-xs text-orange-600 dark:text-orange-400 mt-1">
                      ⚠️ Microsoft Entra requires HTTPS. Access the app via HTTPS (port 8443) to configure SSO.
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-[#5869ff]/30 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-[#1a1f5c]/50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-gradient-to-r from-[#5869ff] to-[#000742] text-white rounded-lg hover:shadow-lg transition-all duration-200"
                >
                  Add Provider
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedProvider && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white dark:bg-[#000742] rounded-xl shadow-2xl p-6 max-w-md w-full border border-gray-200 dark:border-[#5869ff]/20">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Delete Provider
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  This action cannot be undone
                </p>
              </div>
            </div>

            <div className="mb-6">
              <p className="text-gray-700 dark:text-gray-300 mb-3">
                Are you sure you want to delete <strong>{selectedProvider.name}</strong>?
              </p>
              {selectedProvider.active_users.count > 0 && (
                <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                  <p className="text-sm text-yellow-800 dark:text-yellow-300">
                    ⚠️ Warning: This provider has <strong>{selectedProvider.active_users.count} active user{selectedProvider.active_users.count !== 1 ? 's' : ''}</strong>.
                    Deleting it will prevent {selectedProvider.active_users.count !== 1 ? 'them' : 'this user'} from logging in via SSO.
                  </p>
                </div>
              )}
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                disabled={isDeleting}
                className="flex-1 px-4 py-2 border border-gray-300 dark:border-[#5869ff]/30 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-[#1a1f5c]/50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={isDeleting}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isDeleting ? 'Deleting...' : 'Delete Provider'}
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </DashboardLayout>
  )
}